<!-- eslint-disable no-undef -->
<script setup>
  // Imports
  import { ref, onMounted, nextTick } from 'vue';
  import { TrashIcon } from '@heroicons/vue/24/solid';
  import LoadingAnimation3 from '../loadingIcons/LoadingAnimation3.vue';
  // import SimpleDropdown from '../dropdowns/SimpleDropdown.vue';
  import Data from '@/api/data';
  import Calculation from '@/api/calculation';
  import { useModalBackdropClose } from '@/composables/useModalBackdropClose';

  // Definitions
  const props = defineProps({
    dataID: {
      type: String,
      default: null
    },
    subcollectionOptions: {
      type: Object,
      default: () => ({})
    }
  });

  const emit = defineEmits(['calculation-details', 'close']);

  const loading = ref(false);
  const equationDOM = ref(null);
  const subcollections = ref(props.subcollectionOptions);
  const name = ref('');
  const unit = ref('');
  const equation = ref('');
  const startFrom = ref('');
  const variables = ref([{source: '', field: ''}]);
  const error = ref('');
  const mode = ref('equation'); // 'equation' or 'comparison'
  const keepValue = ref(''); // For comparison mode

  // Lifecycle hooks
  onMounted(async() => {
    if (!subcollections.value) {
      await getSubcollections();
    }
  });

  // Methods

  // Get the subcollections and their fields connected to the dataID.
  const getSubcollections = async() => {
    const response = await Data.get(props.dataID);
    if (response.status === 'success') {
      // Ignore subcollections that are not relevant to the timeseries.
      const ignoreSubcollections = ['Net_Cfg', 'action_request', 'action_result', 'location', 'messages']
      subcollections.value = Object.fromEntries(
        Object.entries(response.details).filter(([key]) => !ignoreSubcollections.includes(key))
      );
    }
  }

  const insertAtCursor = (text) => {
    if (!equationDOM.value) return;
    
    const input = equationDOM.value;  // The actual DOM element
    const start = input.selectionStart;  // Can only get cursor position from DOM element
    const end = input.selectionEnd;
    const currentValue = equation.value; // The current text content
    
    equation.value = currentValue.substring(0, start) + text + currentValue.substring(end);
    
    nextTick(() => {
      input.setSelectionRange(start + text.length, start + text.length);
      input.focus();
    });
  };

  const validateEquation = () => {
    // Check for any invalid variable references.
    const variablePattern = /([a-zA-Z_][a-zA-Z0-9_]*)\.[a-zA-Z_][a-zA-Z0-9_]*/g;
    const matches = equation.value.match(variablePattern) || [];
    for (const match of matches) {
      const [source, field] = match.split('.');
      if (!variables.value.some(variable => variable.source === source && variable.field === field)) {
        error.value = `Invalid variable in equation: "${match}"`;
        return false;
      }
    }

    // Check if all defined variables are used in the equation.
    for (const variable of variables.value) {
      const expectedRef = `${variable.source}.${variable.field}`;
      if (!equation.value.includes(expectedRef)) {
        error.value = `Variable "${expectedRef}" is defined but not used in the equation.`;
        return false;
      }
    }

    // Everything checks out.
    return true;
  }

  const handleSubmit = async() => {
    loading.value = true;
    error.value = '';

    // Replace spaces with underscores in the name.
    const formattedName = name.value.replace(/\s+/g, '_');

    // Check if the equation is valid.
    if (!validateEquation()) {
      loading.value = false;
      return;
    }
    const args = {
      dataID: props.dataID,
      name: formattedName,
      equation: equation.value,
      unit: unit.value,
      startDate: startFrom.value,
      values: variables.value
    }

    if (mode.value === 'comparison') {
      args.keepValue = keepValue.value;
    }

    const response = await Calculation.post('', args);

    if (response.status !== 'success') {
      if (response.details?.error && response.details?.equation) {
        error.value = `${response.details.error}. It uses: ${response.details.equation}`;
      }
      else if (response.details?.error && response.details?.name) {
        error.value = `${response.details.error} under name: ${response.details.name}`;
      }
      else {
        error.value = response.details?.error || response.message;
      }
      
      loading.value = false;
      return;
    }

    // Add the new calculation to the subcollections.
    subcollections.value[formattedName] = {
      messageUTC: 'str',
      recieveUTC: 'str',
      value: 'float',
      unit: 'str',
    }

    // Emit the new calculation details and close the modal.
    emit('calculation-details', {name: name.value});
    close();
  }

  const close = () => {
    loading.value = false;
    emit('close');
  };

  const { onBackdropMousedown, onBackdropMouseup, onContentMousedown } = useModalBackdropClose(close);
</script>

<template>
  <div class="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex justify-center items-center z-50" @mousedown.self="onBackdropMousedown" @mouseup.self="onBackdropMouseup">
    <div class="ui-modal p-[20px] rounded-lg w-[600px] max-h-[calc(100vh-50px)] overflow-y-auto" @mousedown="onContentMousedown">
      <div class="h-[3px] w-full bg-mc-red rounded-full mb-5"></div>
      <h2 class="font-extrabold text-xl text-center mb-5 text-mc-ink dark:text-on-dark tracking-tight">Create A New Source</h2>
      <p class="font-normal text-center text-orange-600">WARNING: This will affect all devices that share the same template.</p>
      <br>
      <form @submit.prevent="handleSubmit">

        <!-- Collection name -->
        <div class="mb-4">
          <label for="calc-name" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Name</label>
          <input id="calc-name" v-model="name" type="text" class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20" required />
        </div>

        <!-- Variables -->
        <div v-for="(value, index) in variables" :key="index" class="mt-5 flex gap-2">
          <div v-if="variables.length > 1" class="flex gap-2 items-center hover:cursor-pointer pr-2 pt-5" @click="variables.splice(index, 1)">
            <TrashIcon class="h-[25px] w-[25px]"/>
          </div>

          <!-- Source -->
          <label class="font-bold">
              Source:
            <select :value="value.source" @change="variables[index].source = $event.target.value" class="w-full mt-1 p-1.5 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark font-normal focus-visible:outline-none focus-visible:border-mc-red" required>
              <option v-for="subcollection in Object.keys(subcollections)" :key="subcollection" :value="subcollection">{{ subcollection }}</option>
            </select>
          </label>

          <!-- field -->
          <label class="font-bold w-[175px] mr-3">
            Value: <span v-if="!value.source" class="font-normal text-red-600">(select source)</span>
            <select :value="value.field || ''" @change="variables[index].field = $event.target.value" :disabled="!value.source" class="w-full mt-1 p-1.5 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark font-normal focus-visible:outline-none focus-visible:border-mc-red disabled:opacity-50" required>
              <option v-for="field in Object.keys(subcollections[value.source] ?? []).filter(key => 
                subcollections[value.source][key] === 'int' || 
                subcollections[value.source][key] === 'float')" 
                :key="field" 
                :value="field">
                {{ field }}
              </option>
            </select>
          </label>

          <!-- Add to equation -->
          <button type="button" 
            v-if="value.source && value.field" 
            @click="insertAtCursor(`${value.source}.${value.field}`)" 
            class="default-button w-[60px] h-[40px] text-center p-0 mt-6">
            Insert in equation
          </button>
        </div>

        <!-- Add another variable -->
        <button type="button" @click="variables.push({source: '', field: ''})" class="text-mesh-brand-blue font-bold" :class="{'ml-10': variables.length > 1}">+ Add another value</button>

        <!-- Mode Switch -->
        <div class="mt-5 flex items-center gap-3">
          <span class="font-bold">Mode:</span>
          <label class="inline-flex items-center cursor-pointer">
            <span class="mr-3 text-sm" :class="mode === 'equation' ? 'font-bold' : 'font-normal'">Equation</span>
            <div class="relative">
              <input 
                type="checkbox" 
                class="sr-only peer" 
                :checked="mode === 'comparison'"
                @change="mode = mode === 'equation' ? 'comparison' : 'equation'"
              >
              <div class="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-mesh-brand-blue transition-colors"></div>
              <div class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-5"></div>
            </div>
            <span class="ml-3 text-sm" :class="mode === 'comparison' ? 'font-bold' : 'font-normal'">Comparison</span>
          </label>
        </div>

        <!-- Equation -->
        <div class="mt-5">
          <label class="font-bold">
            {{ mode === 'equation' ? 'Equation:' : 'Comparison:' }}
            <span v-if="variables[0].field && mode === 'equation'" class="font-normal ml-2">example = ({{ variables[0].source }}.{{ variables[0].field }} + 5) / 2</span>
            <input
              ref="equationDOM"
              v-model="equation"
              type="text"
              class="w-full mt-1 p-1.5 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark font-normal focus-visible:outline-none focus-visible:border-mc-red"
              required
            />
          </label>
        </div>

        <!-- Comparison Operator (only in comparison mode) -->
        <div v-if="mode === 'comparison'" class="mt-5">
          <label class="font-bold">
            Target value:
            <select v-model="keepValue" class="w-full mt-1 p-1.5 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark font-normal focus-visible:outline-none focus-visible:border-mc-red" required>
              <option v-for="variable in variables" :key="variable.source + '.' + variable.field" :value="variable.source + '.' + variable.field">
                {{ variable.source }}.{{ variable.field }}
              </option>
            </select>
          </label>
        </div>

        <!-- Unit -->
        <div class="mt-5">
          <label for="calc-unit" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Unit</label>
          <input id="calc-unit" v-model="unit" type="text" class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20" required />
        </div>

        <!-- Start From -->
        <div class="mt-5">
          <label for="calc-start-from" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Start From</label>
          <input id="calc-start-from" v-model="startFrom" type="date" class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20" required />
        </div>

        <!-- Error -->
        <div v-if="error" class="mt-5">
          <p class="font-normal ml-2 text-red-600 whitespace-pre-line">* {{ error }}</p>
        </div>

        <!-- Save and close -->
        <div class="flex justify-between mt-10">
          <button type="button" @click="close" class="px-5 py-2 text-xs rounded-full border border-mc-steel dark:border-white/20 text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/20 dark:hover:bg-white/10 transition-colors duration-160">Cancel</button>
          <LoadingAnimation3 v-if="loading" size="30px"></LoadingAnimation3>
          <button v-else type="submit" :disabled="loading" class="default-button" :class="{'default-button-off': loading}">Create</button>
        </div>
      </form>
    </div>
    <div id="preview">

    </div>
  </div>
</template>
